/**
 * Revelación al posarse, al enfocar y al pulsar. **Capa fina sobre el
 * `Popover` del design system.**
 *
 * Es el patrón de Recuperos —`components/Popover.tsx`—, pero portarlo entero
 * habría sido el error de `UI-5` por tercera vez. Conviene dejar escrito qué
 * resuelve cada cual, porque la primera versión de la auditoría se equivocó
 * justo aquí:
 *
 * | Lo que hacía falta | Quién lo pone |
 * |---|---|
 * | Portal a `document.body`, para no recortarse dentro de una tabla | **El DS.** `PopoverContent` ya envuelve en `Popover.Portal` |
 * | Reposicionar al desplazar y al redimensionar | **El DS.** Radix lo hace con Floating UI |
 * | Encoger el panel en lugar de taparse con el disparador | **El DS.** `--radix-popover-content-available-height`, que el CSS consume |
 * | Cierre con Escape, al pulsar fuera y devolución del foco | **El DS.** |
 * | `role="dialog"`, `aria-expanded`, `aria-controls` | **El DS.** |
 * | **Apertura al posarse, con margen de gracia** | Aquí. Radix `Popover` es solo de clic |
 * | **Acción propia al activar** | Aquí |
 *
 * O sea: de las seis cosas que la auditoría atribuía al `Popover` de Recuperos,
 * cinco ya venían en la caja. Lo que falta de verdad son las dos últimas, y es
 * lo único que este archivo añade.
 *
 * `UI-13`: los tres gestos abren el mismo contenido, porque el hover no existe
 * ni en táctil ni con teclado.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@mpfn/ui'
import { HAY_HOVER, useMedia } from '../lib/medios.js'

/** Retraso antes de abrir al posarse: evita destellos al cruzar una tabla. */
const RETRASO = 150

/**
 * Margen de gracia antes de cerrar.
 *
 * Entre el disparador y el panel hay un hueco —el `sideOffset` de 6 px del
 * design system—, y al cruzarlo el puntero no está sobre ninguno de los dos.
 * Cerrar en ese instante dejaba el panel **inalcanzable**: no se podía
 * desplazar por dentro ni pulsar nada. Es el mismo valor y el mismo motivo que
 * en Recuperos.
 */
const GRACIA = 120

interface PropsSeguimiento {
  /** Contenido del disparador. El `PopoverTrigger` del DS ya es un `<button>`. */
  disparador: ReactNode
  /** Qué se abre y qué pasa al activarlo. Es el nombre accesible del botón. */
  etiqueta: string
  children: ReactNode
  /**
   * Acción principal al pulsar, sin perder la vista previa al posarse.
   *
   * Cuando se pasa, el clic **no** fija el panel: ejecuta esto. Es el caso de
   * la fila de documento, donde posarse muestra el circuito y pulsar abre el
   * documento.
   */
  onActivar?: () => void
  className?: string
}

export function Seguimiento({
  disparador,
  etiqueta,
  children,
  onActivar,
  className,
}: PropsSeguimiento) {
  const hayHover = useMedia(HAY_HOVER)
  const [abierto, setAbierto] = useState(false)
  const temporizador = useRef<ReturnType<typeof setTimeout>>(undefined)
  /**
   * Si el usuario PIDIÓ el panel —pulsándolo— o solo se lo encontró.
   *
   * Decide dos cosas, y las dos por el mismo motivo: posarse y enfocar son
   * gestos de paso, y pulsar es una intención.
   *
   *  · **Si el panel se lleva el foco.** Radix lo enfoca al abrir, que es lo
   *    correcto para un `dialog` que se pidió. Al posarse le arrancaría el foco
   *    al usuario sin que hiciera nada; y al tabular por una tabla era peor
   *    todavía —el foco entraba al panel, el disparador perdía el foco, el
   *    cierre con gracia lo cerraba y el foco se quedaba en el aire—.
   *  · **Si salir del disparador lo cierra.** Pedido, se queda hasta Escape o
   *    un clic fuera, como cualquier desplegable.
   *
   * Con teclado, entonces: al tabular el panel se revela sin robar el foco, y
   * quien quiera entrar en él pulsa Intro.
   */
  const pedido = useRef(false)

  useEffect(
    () => () => {
      clearTimeout(temporizador.current)
    },
    [],
  )

  const abrirConRetraso = () => {
    clearTimeout(temporizador.current)
    temporizador.current = setTimeout(() => {
      setAbierto(true)
    }, RETRASO)
  }

  const cerrarConGracia = () => {
    clearTimeout(temporizador.current)
    if (pedido.current) return
    temporizador.current = setTimeout(() => {
      setAbierto(false)
    }, GRACIA)
  }

  const mantener = () => {
    clearTimeout(temporizador.current)
  }

  // Solo donde el puntero puede posarse. En táctil estos gestos no llegan, y
  // los navegadores que los emulan al tocar abrirían el panel además de
  // ejecutar el clic.
  const gestosDelDisparador = hayHover
    ? { onPointerEnter: abrirConRetraso, onPointerLeave: cerrarConGracia }
    : {}
  // En el panel, entrar CANCELA el cierre pendiente; no vuelve a abrir. Es la
  // otra mitad del margen de gracia: sin esto, cruzar el hueco cerraba igual.
  const gestosDelPanel = hayHover
    ? { onPointerEnter: mantener, onPointerLeave: cerrarConGracia }
    : {}

  return (
    <Popover
      open={abierto}
      onOpenChange={(valor) => {
        clearTimeout(temporizador.current)
        // Escape, pulsar fuera y el foco saliendo pasan por aquí. Todos dejan
        // el panel sin pedir, para que la próxima apertura vuelva a decidir.
        if (!valor) pedido.current = false
        setAbierto(valor)
      }}
    >
      <PopoverTrigger
        // `mpfn-control`: el contrato de foco del design system. Se pone la
        // clase en lugar de escribir el `outline`, que es lo que `UI-5` pide y
        // lo que ya hacen `Breadcrumb` y `AcordeonRequerimiento` aquí mismo.
        className={['sigfisc-seguimiento__disparador', 'mpfn-control', className]
          .filter(Boolean)
          .join(' ')}
        aria-label={etiqueta}
        {...gestosDelDisparador}
        onClick={(evento) => {
          /*
           * `stopPropagation` SIEMPRE, y es lo primero.
           *
           * Este disparador vive dentro de una fila de `DataTable`, que lleva
           * su propio `onClick` para abrir el registro. Sin cortar aquí, pulsar
           * el chip hacía las dos cosas: alternaba el panel y **navegaba**, con
           * lo que el panel desaparecía con la pantalla. Se comprobó pulsando
           * de verdad, no simulando el evento.
           */
          evento.stopPropagation()

          if (!onActivar) {
            /*
             * Sin acción propia, pulsar FIJA el panel. Y fijar no es alternar:
             * si está abierto porque el puntero está encima —revelado, no
             * pedido—, pulsar tiene que dejarlo puesto. Alternar sin más lo
             * hacía desaparecer justo al pulsarlo, que es lo contrario de lo
             * que el gesto pide. Solo se cierra al volver a pulsar uno ya fijo.
             */
            if (abierto && !pedido.current) {
              evento.preventDefault()
              pedido.current = true
              clearTimeout(temporizador.current)
              return
            }
            pedido.current = !abierto
            return
          }
          // `preventDefault` corta el alternar de Radix, que compone su propio
          // manejador sobre este y lo respeta si el evento ya está cancelado.
          evento.preventDefault()
          clearTimeout(temporizador.current)
          pedido.current = false
          setAbierto(false)
          onActivar()
        }}
        // La fila de la tabla escucha Intro y Espacio para abrir el documento
        // (`DataTable` se los pone al `<tr>`). Sin esto, pulsar Intro sobre el
        // disparador hacía las dos cosas: abría el panel y navegaba.
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' || evento.key === ' ') evento.stopPropagation()
        }}
        onFocus={() => {
          setAbierto(true)
        }}
        onBlur={cerrarConGracia}
      >
        {disparador}
      </PopoverTrigger>

      <PopoverContent
        className="sigfisc-seguimiento"
        align="start"
        onOpenAutoFocus={(evento) => {
          if (!pedido.current) evento.preventDefault()
        }}
        {...gestosDelPanel}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}
